import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";

import { modifiersDeleteById, modifiersGetModifierGroup } from "~/api-refs";
import { ModalLoadingDialog } from "~/components/shared/modal-loading-dialog";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "~/components/ui/dialog";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

export const Route = createFileRoute(
  "/_authenticated/menus/$menuId/modifiers/$modifierGroupId/delete"
)({
  component: DeleteModifierGroupModal,
});

function DeleteModifierGroupModal() {
  const { menuId, modifierGroupId } = Route.useParams();
  const navigate = useNavigate();

  // Fetch modifier group data
  const { data: modifierGroup, isPending } = useQuery(
    convexQuery(modifiersGetModifierGroup, {
      modifierGroupId: toConvexTableId("modifierGroups", modifierGroupId),
    })
  );

  const deleteModifierMutation = useMutation({
    mutationFn: ({
      modifierGroupId: groupId,
    }: {
      modifierGroupId: Id<"modifierGroups">;
    }) => {
      return convexClient.mutation(modifiersDeleteById, {
        modifierGroupId: groupId,
      });
    },
    onSuccess: () => {
      ProductEvents.modifierGroupDeleted(modifierGroupId);
    },
    onError: (error) => {
      logger.exception("Failed to delete modifier", error);
    },
  });

  const handleClose = () => {
    void navigate({ to: `/menus/${menuId}/modifiers` });
  };

  const handleDelete = async () => {
    if (deleteModifierMutation.isPending || !modifierGroup) {
      return;
    }
    try {
      await deleteModifierMutation.mutateAsync({
        modifierGroupId: toConvexTableId("modifierGroups", modifierGroup._id),
      });
      toast.success(`Modifier group "${modifierGroup.name}" has been deleted`);
      // Navigate to modifiers list
      await navigate({ to: `/menus/${menuId}/modifiers` });
    } catch {
      toast.error("Failed to delete modifier group");
    }
  };

  if (isPending || !modifierGroup) {
    return (
      <ModalLoadingDialog
        message="Loading modifier group..."
        onOpenChange={handleClose}
      />
    );
  }

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent className="max-w-lg">
        <div className="flex w-full">
          <div className="flex w-full flex-col items-center">
            <DialogTitle className="h2 text-foreground w-fit leading-9">
              Delete Group
            </DialogTitle>
            <h3 className="text-muted-foreground mt-1 text-center text-balance">
              Are you sure you want to delete {modifierGroup?.name}?
            </h3>
            <div className="mt-5 flex w-full gap-3">
              <Button
                className="w-full"
                onClick={handleClose}
                type="button"
                variant="ghost"
              >
                Cancel
              </Button>
              <Button
                className="w-full"
                disabled={deleteModifierMutation.isPending}
                onClick={handleDelete}
                type="button"
                variant="destructive"
              >
                Delete Group
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
